(ns ductile.ui.agent.tools
  "Tool registry and execution for the AI agent.

   Tools are defined as data with associated handler functions.
   This allows centralized tool configuration and easier testing."
  (:require [clojure.string :as str]
            [ductile.ui.agent.policy :as agent.policy]
            [ductile.ui.code :as code]
            [ductile.ui.locale :as locale :refer [tr]]
            [malli.core :as m]))

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Tool Name Utilities

(def ^:private tool-name->label
  {"vehicle-search" "Vehicle search"
   "query-one" "Single Vehicle"
   "count-vehicles" "Vehicle count"
   "resolve-query-intent" "Intent resolver"
   "resolve-attribute-intent" "Attribute resolver"
   "eval-clojure" "Computation"
   "display-artifact" "Artifact rendering"})

(defn- humanize-tool-name [tool-name]
  (->> (str/split (or tool-name "") #"-")
       (remove str/blank?)
       (map str/capitalize)
       (str/join " ")))

(defn tool-label [tool-name]
  (tr (or (get tool-name->label tool-name)
          (humanize-tool-name tool-name)
          tool-name)))

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Dataset Management

(defn user-requested-visualization?
  "Check if user explicitly requested a visualization."
  [text]
  (boolean (re-find #"\b(chart|graph|plot|visuali[sz]e?|visualization|table|artifact)\b"
                    (str/lower-case (str text)))))

(defn last-user-message-text
  "Get the text of the last user message from the message list."
  [messages]
  (some->> messages
           (some (fn [{:keys [role content]}]
                   (when (= "user" role) content)))))

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Tool Result Compaction

(defn vehicle-search-compact
  "Compact vehicle-search result for LLM."
  [result register-dataset-fn]
  (let [fahrzeuge (or (:fahrzeuge result) [])
        dataset-id (register-dataset-fn :fahrzeuge fahrzeuge)]
    {:total-count (:total-count result)
     :returned-count (:returned-count result)
     :truncated? (:truncated? result)
     :dataset-id dataset-id
     :dataset-kind "fahrzeuge"
     :llm/note "No vehicle attributes included. You MUST call eval-clojure to read any attribute values from this dataset."}))

(defn vehicle-search-artifact
  "Create artifact from vehicle-search result."
  [result]
  (cond
    (and (sequential? (:fahrzeuge result))
         (> (count (:fahrzeuge result)) 1))
    {:id (str (random-uuid))
     :type "table"
     :payload {:rows (vec (:fahrzeuge result))
               :search-params (:search-params result)}
     :title (tr "Vehicle search results")
     :caption (when (:truncated? result)
                (tr "Showing %1 of %2 vehicles" (:returned-count result) (:total-count result)))
     :status :ready
     :created-at (.now js/Date)}

    (and (sequential? (:fahrzeuge result))
         (= 1 (count (:fahrzeuge result))))
    (let [entity (first (:fahrzeuge result))]
      {:id (str (random-uuid))
       :type "fahrzeug"
       :payload {:fahrzeug entity
                 :search-params (:search-params result)}
       :title (or (some-> entity :vehicle/vin str) (tr "Vehicle"))
       :status :ready
       :created-at (.now js/Date)})
    :else nil))

(defn query-one-compact
  "Compact query-one result for LLM."
  [result register-dataset-fn]
  (if (and (:found result) (map? (:fahrzeug result)))
    (let [fahrzeug (:fahrzeug result)
          dataset-id (register-dataset-fn :fahrzeug fahrzeug)]
      {:found true
       :dataset-id dataset-id
       :dataset-kind "fahrzeug"
       :vehicle/vin (:vehicle/vin fahrzeug)
       :llm/note "No attributes included. You MUST call eval-clojure to read ANY attribute value. NEVER guess from training knowledge."})
    result))

(defn query-one-artifact
  "Create artifact from query-one result."
  [result]
  (when (and (:found result) (map? (:fahrzeug result)))
    {:id (str (random-uuid))
     :type "fahrzeug"
     :payload {:fahrzeug (:fahrzeug result)}
     :title (or (some-> result :fahrzeug :vehicle/vin str) (tr "Vehicle"))
     :status :ready
     :created-at (.now js/Date)}))

(defn count-vehicles-compact
  "Compact count-vehicles result for LLM."
  [result]
  {:count (:count result)
   :llm/note "Count only. Use vehicle-search + eval-clojure for grouping."})

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Eval Clojure Tool

(def ^:private max-eval-top-entries 30)
(def ^:private max-value-preview-chars 400)

(def ^:private eval-clojure-params-schema
  [:map
   [:code :string]
   [:dataset-id {:optional true} [:maybe :uuid-string]]])

(defn- validate-eval-clojure-params
  "Validate eval-clojure parameters using Malli.
   Returns nil if valid, or a validation error map if invalid."
  [params]
  (when-let [explanation (m/explain eval-clojure-params-schema params)]
    (let [errors (->> (:errors explanation)
                      (mapv (fn [e]
                              {:path (vec (:path e))
                               :in (:in e)
                               :value (:value e)
                               :message (:message e)})))
          dataset-id-error (some #(when (= [:dataset-id] (:in %)) %) errors)]
      {:ok false
       :error :validation-error
       :error-message (cond
                        dataset-id-error
                        (let [bad-value (:value dataset-id-error)]
                          (str "Invalid dataset-id: '" bad-value
                               "'. Must be a valid UUID string (e.g., '550e8400-e29b-41d4-a716-446655440000') "
                               "or null/omitted to use the latest dataset."))
                        :else
                        (str "Invalid parameters for eval-clojure: "
                             (str/join ", " (mapv :message errors))))
       :validation-errors errors})))

(defn- sanitize-for-sci [v]
  (cond
    (nil? v) nil
    (or (string? v) (number? v) (boolean? v) (keyword? v)) v
    (instance? js/Date v) (.toISOString v)
    (map? v) (into {} (map (fn [[k val]] [(sanitize-for-sci k) (sanitize-for-sci val)])) v)
    (vector? v) (mapv sanitize-for-sci v)
    (set? v) (mapv sanitize-for-sci v)
    (and (sequential? v) (not (string? v))) (mapv sanitize-for-sci v)
    :else (str v)))

(defn eval-clojure
  "Evaluate ClojureScript code client-side via SCI.
   Parameters:
   - code: ClojureScript expression to evaluate (required)
   - dataset-id: Optional UUID string from previous tool result, or null/omitted for latest dataset"
  [{:keys [code dataset-id] :as params} tool-datasets latest-dataset-id]
  (try
    ;; Validate parameters first
    (when-let [validation-error (validate-eval-clojure-params params)]
      (throw (ex-info (:error-message validation-error)
                      (assoc validation-error :dataset-id dataset-id))))
    (let [selected-dataset-id (or dataset-id latest-dataset-id)
          selected-kind (get-in tool-datasets [selected-dataset-id :kind])
          latest-data (get-in tool-datasets [selected-dataset-id :data])
          latest-data (if (and (= selected-kind :fahrzeuge)
                               (agent.policy/direct-key-access-on-data? code)
                               (sequential? latest-data)
                               (= 1 (count latest-data))
                               (map? (first latest-data)))
                        (first latest-data)
                        latest-data)
          _ (when (nil? latest-data)
              (throw (ex-info "No dataset loaded. Run vehicle-search or query-one first."
                              {:error :missing-dataset
                               :error-message "No dataset loaded for eval-clojure."
                               :dataset-id selected-dataset-id})))
          wrapped-code (str "(let [data " (pr-str (sanitize-for-sci latest-data))
                            " latest-dataset-id " (pr-str selected-dataset-id)
                            "] " code ")")
          result (code/eval-string wrapped-code)
          coll-result? (or (map? result)
                           (set? result)
                           (and (sequential? result) (not (string? result))))
          result-seq (when coll-result? (seq result))
          result-kind (cond
                        (map? result) "map"
                        (set? result) "set"
                        (and (sequential? result) (not (string? result))) "sequence"
                        :else "scalar")
          result-dataset-id (when coll-result?
                              (str (random-uuid)))
          value-preview (let [s (pr-str result)]
                          (if (> (count s) max-value-preview-chars)
                            (str (subs s 0 max-value-preview-chars) "…")
                            s))
          top-entries (when (map? result)
                        (->> result
                             (sort-by (fn [[k _]] (str k)))
                             (take max-eval-top-entries)
                             (mapv (fn [[k v]] {:key (str k)
                                                :value (if (or (map? v)
                                                               (set? v)
                                                               (and (sequential? v) (not (string? v))))
                                                         (pr-str v)
                                                         v)}))))
          columns (when (and result-seq
                             (every? map? (take 20 result-seq)))
                    (->> (take 20 result-seq) (mapcat keys) distinct vec))]
      {:ok true
       :source-dataset-id selected-dataset-id
       :dataset-id result-dataset-id
       :result-kind result-kind
       :item-count (when coll-result? (count result))
       :non-empty? (when coll-result? (boolean (seq result-seq)))
       :row-count (when (and (sequential? result) (not (string? result))) (count result))
       :entry-count (when (map? result) (count result))
       :columns columns
       :top-entries top-entries
       :value-preview value-preview
       :llm/note "Eval result is the source of truth. Report the value-preview exactly as shown."})
    (catch :default e
      (if-let [data (ex-data e)]
        (merge {:ok false} data)
        {:ok false
         :error "eval-error"
         :error-message (ex-message e)}))))

(defn eval-clojure-compact
  "Compact eval-clojure result for LLM."
  [result]
  (select-keys result [:ok :source-dataset-id :dataset-id :result-kind :item-count
                       :non-empty? :row-count :entry-count :columns :top-entries
                       :value-preview :error :error-message :recommended-key
                       :referenced-keys :original-code :effective-code :note
                       :resolved-intent :llm/note]))

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Display Artifact Tool

(defn display-artifact-ui-payload
  "Build UI payload for display-artifact tool."
  [tool-params result]
  (let [ok? (:ok result)]
    {:id (or (:artifact-id result) (str (random-uuid)))
     :type (:artifact/type tool-params)
     :spec (:artifact/spec tool-params)
     :payload (:artifact/payload tool-params)
     :title (or (:artifact/title tool-params)
                (:title (:artifact/spec tool-params))
                (:title (:artifact/payload tool-params)))
     :caption (:artifact/caption tool-params)
     :status (if ok? :ready :error)
     :error-message (:error-message result)
     :created-at (.now js/Date)}))

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Tool Registry

(def ^:private tool-registry
  {"vehicle-search"
   {:label "Vehicle search"
    :cacheable? true}

   "query-one"
   {:label "Single Vehicle"
    :cacheable? true}

   "count-vehicles"
   {:label "Vehicle count"
    :cacheable? true}

   "resolve-query-intent"
   {:label "Intent resolver"
    :cacheable? true}

   "resolve-attribute-intent"
   {:label "Attribute resolver"
    :cacheable? true}

   "display-artifact"
   {:label "Artifact rendering"
    :cacheable? true}

   "eval-clojure"
   {:label "Computation"
    :cacheable? false}})

(defn tool-cacheable?
  "Check if a tool's results can be cached."
  [tool-name]
  (if-let [tool (get tool-registry tool-name)]
    (:cacheable? tool)
    false))

(defn get-tool-label
  "Get the display label for a tool."
  [tool-name]
  (if-let [tool (get tool-registry tool-name)]
    (:label tool)
    (tool-label tool-name)))
